// 에러 바운더리 컴포넌트
// React 앱에서 발생하는 에러를 잡아서 사용자에게 친절한 메시지 표시
import React, { Component } from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false, // 에러 발생 여부
      error: null,     // 에러 객체
      errorInfo: null, // 에러 상세 정보
    };
  }

  // 에러가 발생하면 이 메서드가 호출됩니다
  static getDerivedStateFromError(error) {
    // 다음 렌더링에서 폴백 UI를 표시하도록 상태 업데이트
    return { hasError: true };
  }

  // 에러 정보를 기록하는 메서드
  componentDidCatch(error, errorInfo) {
    // 에러 로깅 서비스에 전송할 수 있습니다 (예: Sentry)
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  // 페이지 새로고침으로 에러 복구 시도
  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // 에러 발생 시 표시할 UI
      return (
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              textAlign: 'center',
              gap: 2,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 80, color: 'error.main' }} />
            <Typography variant="h4" component="h1" gutterBottom>
              앗! 문제가 발생했습니다
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              일시적인 오류가 발생했습니다.
              <br />
              페이지를 새로고침하면 해결될 수 있습니다.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={this.handleReload}
            >
              페이지 새로고침
            </Button>

            {/* 개발 모드에서만 에러 상세 정보 표시 */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box
                sx={{
                  mt: 4,
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  textAlign: 'left',
                  width: '100%',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <Typography variant="caption" component="pre" sx={{ fontSize: '0.75rem' }}>
                  {this.state.error.toString()}
                  {'\n\n'}
                  {this.state.errorInfo?.componentStack}
                </Typography>
              </Box>
            )}
          </Box>
        </Container>
      );
    }

    // 에러가 없으면 자식 컴포넌트를 정상 렌더링
    return this.props.children;
  }
}

export default ErrorBoundary;
